import React from 'react'
import { connect } from 'react-redux'
import { CSSTransition } from 'react-transition-group'

import { RootStateTypeExtra } from '@store/types'

import PropertiesListSkeleton from '@components/compare-property/skeleton'
import usePropertyCompareFirebaseTrigger from '@components/compare-property/utils'
import DataHandler, { Status } from '@components/data-handler/data-handler'

import {
  LotInterface,
  selectMatrixDataFromResult,
  useGetMatrixDataByPrecinctQuery,
} from '@api/houseAndLand'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { PropertyType } from '@adUtilities/types'

import PropertiesList, { Property } from './properties-list'

interface AddActionMode {
  actionMode: 'add'
  replaceIndex?: number
}

interface ReplaceActionMode {
  actionMode: 'replace'
  replaceIndex: number
}

interface ExtendedLotInterface extends LotInterface {
  stageId: string
  precinctId: string
}

type LotsListProps = {
  className?: string
  containerClassName?: string
  projectName: string
  activePrecinctId?: string
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  onActionComplete?: () => void
  selectedPrecinct?: string
} & (AddActionMode | ReplaceActionMode)

function LotsList({
  className = '',
  containerClassName = '',
  projectName,
  activePrecinctId,
  comparePropertyProps,
  actionMode,
  replaceIndex,
  onActionComplete,
  selectedPrecinct,
}: LotsListProps) {
  const {
    checkPropertyComparability,
    checkPropertyPositionInCompareMode,
    validateAdd,
    validateReplace,
  } = comparePropertyProps

  const precinctId = selectedPrecinct ?? activePrecinctId

  const matrixDataPayload = useGetMatrixDataByPrecinctQuery(
    {
      projectName,
      precinctIdOrLabel: precinctId || '',
    },
    {
      selectFromResult: selectMatrixDataFromResult,
      skip: !precinctId,
    }
  )

  const { triggerComparisonAdd, triggerComparisonReplace } =
    usePropertyCompareFirebaseTrigger({
      comparePropertyProps,
    })

  const properties = React.useMemo<ExtendedLotInterface[]>(() => {
    if (matrixDataPayload.status !== Status.FULFILLED || !precinctId) {
      return []
    }
    const stagesMap = matrixDataPayload.matrixData?.stages || {}
    const allStages = Object.keys(stagesMap).map((key) => stagesMap[key])

    const lots: ExtendedLotInterface[] = []

    allStages.forEach((stage) => {
      stage.lots.forEach((lot) => {
        lots.push({
          ...lot,
          precinctId,
          stageId: stage.label,
        })
      })
    })
    return lots
  }, [matrixDataPayload.status, precinctId])

  const validateAction = React.useCallback(
    (data: ExtendedLotInterface) => {
      if (
        !checkPropertyComparability({
          data,
          propertyType: PropertyType.Lot,
        })
      ) {
        return false
      }
      if (actionMode === 'add') {
        const { validated } = validateAdd({
          property: {
            data,
            propertyType: PropertyType.Lot,
            references: {
              precinctId: data.precinctId,
              stageId: data.stageId,
            },
          },
        })
        return validated
      }
      if (actionMode === 'replace' && replaceIndex !== undefined) {
        const { validated } = validateReplace({
          property: {
            data,
            propertyType: PropertyType.Lot,
            references: {
              precinctId: data.precinctId,
              stageId: data.stageId,
            },
          },
          index: replaceIndex,
        })
        return validated
      }
      return false
    },
    [actionMode, validateAdd, validateReplace, replaceIndex]
  )

  const propertiesListProp = React.useMemo<Property[]>(
    () =>
      properties.map<Property>((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        status: item.status,
        canAction: validateAction(item),
        selected: !!checkPropertyPositionInCompareMode(item.id),
      })),
    [properties, validateAction, checkPropertyPositionInCompareMode]
  )

  const handleAdd = React.useCallback(
    (property: ExtendedLotInterface) => {
      const propertyPositionInCompareMode = checkPropertyPositionInCompareMode(
        property.id
      )
      if (propertyPositionInCompareMode) {
        return
      }
      triggerComparisonAdd({
        property: {
          propertyType: PropertyType.Lot,
          data: property,
          references: {
            precinctId: property.precinctId,
            stageId: property.stageId,
          },
        },
      })
      onActionComplete?.()
    },
    [checkPropertyPositionInCompareMode, triggerComparisonAdd, onActionComplete]
  )

  const handleReplace = React.useCallback(
    (property: ExtendedLotInterface) => {
      const propertyPositionInCompareMode = checkPropertyPositionInCompareMode(
        property.id
      )
      if (
        propertyPositionInCompareMode ||
        actionMode !== 'replace' ||
        replaceIndex === undefined
      ) {
        return
      }
      triggerComparisonReplace({
        property: {
          propertyType: PropertyType.Lot,
          data: property,
          references: {
            precinctId: property.precinctId,
            stageId: property.stageId,
          },
        },
        index: replaceIndex,
      })
      onActionComplete?.()
    },
    [
      checkPropertyPositionInCompareMode,
      triggerComparisonReplace,
      actionMode,
      replaceIndex,
      onActionComplete,
    ]
  )

  const handleClick = React.useCallback(
    (propertyId: string) => {
      const matchedProperty = properties.find((item) => item.id === propertyId)
      if (!matchedProperty) {
        return
      }

      if (actionMode === 'add') {
        handleAdd(matchedProperty)
      }

      if (actionMode === 'replace') {
        handleReplace(matchedProperty)
      }
    },
    [properties, actionMode, handleAdd, handleReplace]
  )

  return (
    <CSSTransition
      classNames="transition-property-compare-toggle"
      timeout={300}
      in
      appear
      mountOnEnter
      unmountOnExit
    >
      <div className={`h-full w-full ${className}`}>
        <div
          className={`custom-scrollbar h-full w-full overflow-y-auto ${containerClassName}`}
        >
          <DataHandler
            payloads={{
              ...matrixDataPayload,
              data: matrixDataPayload.matrixData,
            }}
            skeletonFrame={<PropertiesListSkeleton />}
          >
            <PropertiesList
              data={propertiesListProp}
              onClick={handleClick}
              labelPrefix="Lot "
            />
          </DataHandler>
        </div>
      </div>
    </CSSTransition>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectName: projectIdentity.projectName,
    activePrecinctId: session?.houseAndLand?.activePrecinctId,
  })
)(LotsList)
